// The estate has no shared notification system yet; this is the smallest
// honest one — a module store + a host the App mounts. It replaces Mantine's
// `notifications.show` at the ported call sites (ADR-0010 keeps Mantine out).
import { useSyncExternalStore } from 'react'

export interface Toast {
  id: number
  color: 'green' | 'red' | 'yellow' | 'blue'
  title: string
  message?: string
}

let toasts: Toast[] = []
let seq = 0
const listeners = new Set<() => void>()

function emit() {
  for (const l of listeners) l()
}

export function showToast(t: Omit<Toast, 'id'>): void {
  const toast = { ...t, id: ++seq }
  toasts = [...toasts, toast]
  emit()
  // errors linger longer: they are the ones someone has to read
  const ttl = t.color === 'red' ? 9000 : 4500
  setTimeout(() => {
    toasts = toasts.filter((x) => x.id !== toast.id)
    emit()
  }, ttl)
}

const TONES: Record<Toast['color'], string> = {
  green: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  red: 'border-red-300 bg-red-50 text-red-900',
  yellow: 'border-amber-300 bg-amber-50 text-amber-900',
  blue: 'border-blue-300 bg-blue-50 text-blue-900',
}

export function ToastHost() {
  const items = useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    () => toasts,
  )
  if (!items.length) return null
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-80 flex-col gap-2">
      {items.map((t) => (
        <div
          key={t.id}
          className={`pointer-events-auto rounded-md border p-3 text-sm shadow-lg ${TONES[t.color]}`}
          role="status"
        >
          <p className="font-semibold">{t.title}</p>
          {t.message ? <p className="mt-0.5 break-words text-xs">{t.message}</p> : null}
        </div>
      ))}
    </div>
  )
}
